import React, { useState, useEffect, useMemo } from 'react';
import { ClipboardDocumentListIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { GasContract } from '../types/gas';
import { gasContractService } from '../services/gasService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';

const GasContracts: React.FC = () => {
  const { t } = useLanguage();
  const [contracts, setContracts] = useState<GasContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const data = await gasContractService.getAllGasContracts();
      setContracts(data);
    } catch (error) {
      console.error('Error loading gas contracts:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.contracts') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
  }, []);

  const filteredContracts = useMemo(() => {
    if (!searchTerm.trim()) return contracts;
    const term = searchTerm.toLowerCase();
    return contracts.filter(contract =>
      contract.contractNumber?.toLowerCase().includes(term) ||
      contract.contractType.toLowerCase().includes(term) ||
      contract.startDate.includes(term) ||
      contract.endDate?.includes(term) ||
      contract.notes?.toLowerCase().includes(term)
    );
  }, [contracts, searchTerm]);

  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentContracts = filteredContracts.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'energy': return 'Energia';
      case 'rhd_variable': return 'RHD forgalom-arányos';
      case 'rhd_fixed': return 'RHD fix';
      default: return type;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'active': return 'Aktív';
      case 'expired': return 'Lejárt';
      case 'terminated': return 'Felmondott';
      default: return status;
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4">
      <CommonPageHeader
        title={t('gas.sections.contracts')}
        subtitle={t('gas.list.subtitle', { count: contracts.length })}
        icon={ClipboardDocumentListIcon}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('gas.list.search.contracts')}
          />
        </div>
        <button
          onClick={loadContracts}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          {t('gas.list.refresh')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.contract.number')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.contract.type')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.contract.fields.startDate')} / {t('gas.contract.fields.endDate')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.contract.fields.capacityKwh')} / {t('gas.contract.fields.price')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.contract.linkedPods')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.contract.status')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentContracts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('gas.list.noResults')}
                  </td>
                </tr>
              ) : (
                currentContracts.map(contract => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-gray-900">{contract.contractNumber || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{getTypeLabel(contract.contractType)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">{contract.startDate}</p>
                      <p className="text-xs text-gray-500">{contract.endDate ? `→ ${contract.endDate}` : 'Folyamatos'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">
                        {contract.capacityKwh ? `${contract.capacityKwh} kWh` : ''}
                        {contract.capacityM3 ? ` / ${contract.capacityM3} m³` : ''}
                        {!contract.capacityKwh && !contract.capacityM3 && '—'}
                      </p>
                      <p className="text-xs text-gray-500">{contract.price ? `${contract.price} ${contract.priceUnit}` : ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{contract.gasPodIds?.length || 0} POD</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        contract.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : contract.status === 'draft'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-red-100 text-red-700'
                      }`}>
                        {getStatusLabel(contract.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={filteredContracts.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />
    </div>
  );
};

export default GasContracts;
